if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();

        const sectionId = this.dataset.sectionId;

        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${sectionId}`);
        this.hiddenControlField = this.querySelector(`#Recipient-control-${sectionId}`);
        this.emailInput = this.querySelector(`#Recipient-email-${sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${sectionId}`);
        this.send_onInput = this.querySelector(`#Recipient-send-on-${sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${sectionId}`);

        // Set timezone offset
        if (this.offsetProperty) {
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');
        this.defaultErrorHeader = this.errorMessage?.innerText;
        this.currentProductVariantId = this.dataset.productVariantId;

        // Store bound event handlers for proper cleanup
        this._onEmailInput = this._handleEmailInput.bind(this);
        this._onDateInput = this.validateDateField.bind(this);
        this._onDateChange = this.validateDateField.bind(this);

        // Bind event handlers
        this.addEventListener('change', this.onChange.bind(this));

        // Initialize form state
        this.onChange();
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;
      cartErrorUnsubscriber = undefined;

      _handleEmailInput() {
        if (this.emailInput.checkValidity() && this.emailInput.value.trim()) {
          this.clearFieldError('email');
        }
      }

      connectedCallback() {
        // Only subscribe if the global functions exist
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source === 'product-form' && event.productVariantId?.toString() === this.currentProductVariantId) {
              this.resetRecipientForm();
            }
          });

          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data?.sectionId === this.dataset.sectionId) {
              this.currentProductVariantId = event.data.variant?.id?.toString();
            }
          });

          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
            if (event.source === 'product-form' && event.productVariantId?.toString() === this.currentProductVariantId) {
              this.displayErrorMessage(event.message, event.errors);
            }
          });
        }

        // Clear email error when email becomes valid again
        if (this.emailInput) {
          this.emailInput.addEventListener('input', this._onEmailInput);
        }

        // Validate date field on input/change
        if (this.send_onInput) {
          this.send_onInput.addEventListener('input', this._onDateInput);
          this.send_onInput.addEventListener('change', this._onDateChange);
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        // Remove event listeners added in connectedCallback
        if (this.emailInput) {
          this.emailInput.removeEventListener('input', this._onEmailInput);
        }

        if (this.send_onInput) {
          this.send_onInput.removeEventListener('input', this._onDateInput);
          this.send_onInput.removeEventListener('change', this._onDateChange);
        }
      }

      hasIncompleteDate() {
        if (!this.send_onInput) return false;

        // If the input has no value but the browser reports it as invalid,
        // it means user typed something incomplete
        if (!this.send_onInput.value && !this.send_onInput.checkValidity()) {
          return true;
        }

        // Check validity.badInput which is true when user enters partial dates
        if (this.send_onInput.validity && this.send_onInput.validity.badInput) {
          return true;
        }

        return false;
      }


      getDateValidationError(dateValue) {
        // Check for incomplete/partial date entry
        if (this.hasIncompleteDate()) {
          return 'Please enter a complete date';
        }

        if (!dateValue) {
          return null; // Empty is allowed (optional field)
        }

        // Check if it matches YYYY-MM-DD format
        const datePattern = /^\d{4}-\d{2}-\d{2}$/;
        if (!datePattern.test(dateValue)) {
          return 'Please enter a valid date';
        }

        // Parse the date and check if it's a real date
        const [year, month, day] = dateValue.split('-').map(Number);
        const parsedDate = new Date(year, month - 1, day);

        // Check if the parsed date matches the input (catches invalid dates like Feb 30)
        if (
          parsedDate.getFullYear() !== year ||
          parsedDate.getMonth() !== month - 1 ||
          parsedDate.getDate() !== day
        ) {
          return 'Please enter a valid date';
        }

        // Check for reasonable year range (not year 22 or 0022)
        if (year < 2020 || year > 2100) {
          return 'Please enter a valid date';
        }

        // Check if date is in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        parsedDate.setHours(0, 0, 0, 0);

        if (parsedDate < today) {
          return 'Send date cannot be in the past';
        }

        return null; // Valid
      }

      validateDateField() {
        if (!this.send_onInput) return;

        this.send_onInput.setCustomValidity('');

        const dateErrorId = `RecipientForm-send_on-error-${this.dataset.sectionId}`;
        const errorElement = this.querySelector(`#${dateErrorId}`);
        const errorTextElement = errorElement?.querySelector('.error-message');

        const errorMessage = this.getDateValidationError(this.send_onInput.value);

        if (errorMessage) {
          // Show error
          if (errorTextElement) {
            errorTextElement.innerText = `${errorMessage}.`;
          }
          if (errorElement) {
            errorElement.classList.remove('hidden');
          }
          this.send_onInput.setAttribute('aria-invalid', 'true');
          this.send_onInput.setAttribute('aria-describedby', dateErrorId);
          this.send_onInput.setCustomValidity(errorMessage);
        } else {
          // Clear error
          if (errorElement) errorElement.classList.add('hidden');
          if (errorTextElement) errorTextElement.innerText = '';
          this.send_onInput.setAttribute('aria-invalid', 'false');
          this.send_onInput.removeAttribute('aria-describedby');
          this.send_onInput.setCustomValidity('');
        }
      }

      onChange() {
        if (!this.checkboxInput) return;

        if (this.checkboxInput.checked) {
          this.enableInputFields();
          if (this.hiddenControlField) {
            this.hiddenControlField.disabled = false;
          }
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          if (this.hiddenControlField) {
            this.hiddenControlField.disabled = true;
          }
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.send_onInput].filter(Boolean);
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty].filter(Boolean);
      }

      clearInputFields() {
        this.inputFields().forEach((field) => {
          if (field) field.value = '';
        });
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => {
          if (field) field.disabled = false;
        });
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => {
          if (field) field.disabled = true;
        });
      }

      checkValidity() {
        // If checkbox is not checked, form is valid (no recipient info needed)
        if (!this.checkboxInput || !this.checkboxInput.checked) {
          return true;
        }

        // Email is required when checkbox is checked
        if (!this.emailInput || !this.emailInput.value || !this.emailInput.value.trim()) {
          return false;
        }

        // Check if email is valid using HTML5 validation
        if (!this.emailInput.checkValidity()) {
          return false;
        }

        // Check for incomplete or invalid date
        if (this.send_onInput) {
          // Check for partial/incomplete date
          if (this.hasIncompleteDate()) {
            return false;
          }

          // Check for invalid date value
          if (this.send_onInput.value) {
            const dateError = this.getDateValidationError(this.send_onInput.value);
            if (dateError) {
              return false;
            }
          }
        }

        return true;
      }

      updateErrors() {
        this.clearErrorMessage();

        // Only show errors if checkbox is checked
        if (!this.checkboxInput || !this.checkboxInput.checked) {
          return;
        }

        const errors = {};

        // Email validation
        if (!this.emailInput || !this.emailInput.value || !this.emailInput.value.trim()) {
          errors.email = ['Recipient email is required'];
        } else if (!this.emailInput.checkValidity()) {
          errors.email = ['Please enter a valid email address'];
        }

        // Date validation
        if (this.send_onInput) {
          const dateError = this.getDateValidationError(this.send_onInput.value);
          if (dateError) {
            errors.send_on = [dateError];
          }
        }

        // Display errors if any
        if (Object.keys(errors).length > 0) {
          this.displayErrorMessage('Please correct the following errors:', errors);
        }
      }

      displayErrorMessage(title, body) {
        this.clearErrorMessage();
        if (!this.errorMessageWrapper) return;

        this.errorMessageWrapper.hidden = false;
        if (typeof body === 'object') {
          if (this.errorMessage) {
            this.errorMessage.innerText = this.defaultErrorHeader || title;
          }
          return Object.entries(body).forEach(([key, value]) => {
            const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
            const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
            const message = Array.isArray(value) ? value.join(', ') : value;
            const errorMessageElement = this.querySelector(`#${errorMessageId}`);
            const errorTextElement = errorMessageElement?.querySelector('.error-message');

            if (errorTextElement) {
              errorTextElement.innerText = `${message}.`;
              errorMessageElement.classList.remove('hidden');
            }

            if (this.errorMessageList) {
              this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message));
            }

            const inputElement = this[`${key}Input`];
            if (inputElement) {
              inputElement.setAttribute('aria-invalid', 'true');
              inputElement.setAttribute('aria-describedby', errorMessageId);
            }
          });
        }

        if (this.errorMessage) {
          this.errorMessage.innerText = body;
        }
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearFieldError(key) {
        const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`;
        const errorMessageElement = this.querySelector(`#${errorMessageId}`);
        const errorTextElement = errorMessageElement?.querySelector('.error-message');

        // Clear the field's error message element
        if (errorMessageElement) {
          errorMessageElement.classList.add('hidden');
        }
        if (errorTextElement) {
          errorTextElement.innerText = '';
        }

        // Clear the input's aria attributes
        const inputElement = this[`${key}Input`];
        if (inputElement) {
          inputElement.setAttribute('aria-invalid', 'false');
          inputElement.removeAttribute('aria-describedby');
        }

        // Remove from error list if present
        if (this.errorMessageList) {
          const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`;
          const listItem = this.errorMessageList.querySelector(`a[href="${fieldSelector}"]`)?.parentElement;
          if (listItem) {
            listItem.remove();
          }

          // Hide the error wrapper if no more errors in the list
          if (this.errorMessageList.children.length === 0 && this.errorMessageWrapper) {
            this.errorMessageWrapper.hidden = true;
          }
        }
      }

      clearErrorMessage() {
        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.hidden = true;
        }

        if (this.errorMessageList) {
          this.errorMessageList.innerHTML = '';
        }

        this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
          field.classList.add('hidden');
          const textField = field.querySelector('.error-message');
          if (textField) textField.innerText = '';
        });

        [this.emailInput, this.messageInput, this.nameInput, this.send_onInput].forEach((inputElement) => {
          if (inputElement) {
            inputElement.setAttribute('aria-invalid', 'false');
            inputElement.removeAttribute('aria-describedby');
          }
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput && this.checkboxInput.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
          this.onChange();
        }
      }
    }
  );
}
